import { useEffect, useState } from 'react';
import { DISPOSITIONS } from '@shared/dispositions';
import { apiRequest } from '@/lib/queryClient';

interface DispositionPadProps {
  /** May be a Twilio SID or a BROWSER-<ts> temp SID; both resolve server-side. */
  callSid: string;
  onSaved: (disposition: string) => void;
  onError: (message: string) => void;
  className?: string;
  /**
   * Where to POST { disposition, note }. Defaults to the one-off call endpoint.
   *
   * The power dialer overrides it with its queue-item endpoint, which records the
   * outcome, mirrors it onto the call record and updates the session counters in
   * one request. Both endpoints take the same body, which is why this is a URL
   * and not a second code path.
   */
  endpoint?: string;
  /** Which outcomes to offer, in order (default: all). */
  values?: readonly string[];
  /** Prefills the note (e.g. the live call notes). */
  initialNote?: string;
}

/**
 * Outcome buttons plus a note, shared by the mobile sheet and the desktop
 * post-call dialog.
 *
 * Saves are awaited and failures are surfaced. The version this replaces fired
 * the request with .catch(() => {}) and toasted "Saved" unconditionally, so a
 * rep had no way to know their tagging was going nowhere.
 */
export function DispositionPad({
  callSid,
  onSaved,
  onError,
  className = '',
  endpoint,
  values,
  initialNote = '',
}: DispositionPadProps) {
  const [note, setNote] = useState(initialNote);
  useEffect(() => { setNote(initialNote); }, [initialNote]);
  const options = values ? values.map((v) => DISPOSITIONS.find((d) => d.value === v)!).filter(Boolean) : DISPOSITIONS;
  const [saving, setSaving] = useState<string | null>(null);

  async function save(disposition: string) {
    setSaving(disposition);
    try {
      await apiRequest(
        'POST',
        endpoint || `/api/calls/${encodeURIComponent(callSid)}/disposition`,
        { disposition, note: note.trim() || undefined },
      );
      setNote('');
      onSaved(disposition);
    } catch (error: any) {
      onError(error?.message || 'Could not save the outcome. Please try again.');
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className={`space-y-3 ${className}`} data-testid="disposition-pad">
      <div className="grid grid-cols-2 gap-2">
        {options.map((d) => (
          <button
            key={d.value}
            type="button"
            disabled={saving !== null}
            onClick={() => save(d.value)}
            className="px-3 py-2.5 rounded-xl border border-border bg-muted/50 hover:bg-muted disabled:opacity-50 text-sm text-left transition-all active:scale-[0.98]"
            data-testid={`disposition-${d.value}`}
          >
            {saving === d.value ? 'Saving…' : d.label}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add a note (optional)"
        rows={3}
        className="w-full px-3 py-2 rounded-xl border border-border bg-muted/50 text-sm resize-none"
        data-testid="disposition-note"
      />
    </div>
  );
}
